import React, { useState, useEffect } from 'react';
import { X, File, Folder, Calendar, User, Users, HardDrive, Tag } from 'lucide-react';
import { FileItem, FileShareRecord } from '../types';
import { formatFileSize, formatDate } from '../utils/fileUtils';
import { SupabaseFileStorage } from '../utils/supabaseFileStorage';

interface DetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: FileItem | null;
  isGlobalDarkMode?: boolean;
  currentUserName?: string;
}

export const DetailsModal: React.FC<DetailsModalProps> = ({
  isOpen,
  onClose,
  item,
  currentUserName
}) => {
  const [shares, setShares] = useState<FileShareRecord[]>([]);

  useEffect(() => {
    if (isOpen && item && item.shared) {
      SupabaseFileStorage.getFileShares(item.id).then(setShares);
    } else {
      setShares([]);
    }
  }, [isOpen, item]);

  if (!isOpen || !item) return null;

  const DetailRow: React.FC<{ label: string; value: string | React.ReactNode; icon?: React.ReactNode }> = ({ label, value, icon }) => (
    <div className="flex items-center gap-3 py-3 border-b border-border">
      <div className="flex items-center gap-2 min-w-[140px]">
        {icon && <div className="text-muted-foreground">{icon}</div>}
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
      </div>
      <div className="flex-1 text-sm text-foreground">{value}</div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-border">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Details</h2>
          <button
            onClick={onClose}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors duration-200"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-muted rounded-lg p-4 mb-6 flex items-center gap-4">
            {item.type === 'folder' ? (
              <Folder size={48} className="text-workspace-accent" />
            ) : (
              <File size={48} className="text-muted-foreground" />
            )}
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-foreground truncate">{item.name}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {item.type === 'folder' ? 'Folder' : 'File'}
              </p>
            </div>
          </div>

          <div className="space-y-1">
            {item.type === 'file' && item.size !== undefined && (
              <DetailRow
                label="Size"
                value={formatFileSize(item.size)}
                icon={<HardDrive size={16} />}
              />
            )}
            
            {item.mimeType && (
              <DetailRow
                label="Type"
                value={item.mimeType}
                icon={<Tag size={16} />}
              />
            )}

            <DetailRow
              label="Created"
              value={formatDate(item.createdAt)}
              icon={<Calendar size={16} />}
            />

            <DetailRow
              label="Modified"
              value={formatDate(item.modifiedAt)}
              icon={<Calendar size={16} />}
            />

            <DetailRow
              label="Owner"
              value={item.ownerName || currentUserName || 'User'}
              icon={<User size={16} />}
            />

            {item.modifiedBy && (
              <DetailRow
                label="Modified by"
                value={item.modifiedBy}
                icon={<User size={16} />}
              />
            )}

            {item.shared && (
              <DetailRow
                label="Shared with"
                icon={<Users size={16} />}
                value={
                  shares.length > 0 ? (
                    <div className="flex -space-x-1.5">
                      {shares.slice(0, 3).map((share, i) => (
                        <div 
                          key={share.id} 
                          className="w-6 h-6 rounded-full border-2 border-card bg-workspace-accent/20 flex items-center justify-center overflow-hidden flex-shrink-0 text-workspace-accent font-semibold text-[10px] relative"
                          style={{ zIndex: 10 - i }}
                          title={share.userName}
                        >
                          {share.userAvatar ? (
                            <img src={share.userAvatar} alt={share.userName} className="w-full h-full object-cover" />
                          ) : (
                            (share.userName || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                          )}
                        </div>
                      ))}
                      {shares.length > 3 && (
                        <div className="w-6 h-6 rounded-full border-2 border-card bg-muted flex items-center justify-center text-[10px] font-medium text-muted-foreground relative z-0">
                          +{shares.length - 3}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-muted-foreground text-sm">Loading...</span>
                  )
                }
              />
            )}
          </div>
        </div>

        <div className="p-6 border-t border-border flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-workspace-accent hover:bg-workspace-accent-hover text-white rounded-lg transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

